import { useEffect, useMemo, useState } from 'react';
import { getBytes, ref as storageRef } from 'firebase/storage';
import type { StatutePreviewUiSection } from '../components/StatutePreviewReader';
import { storage } from '../lib/firebase';
import { adaptStatuteAny } from '../lib/statute/detect';
import { parseStatuteTextForReader } from '../lib/statuteFormat';
import { normalizePreviewSections } from '../lib/statuteSections';
import { toPreviewSections } from '../lib/statute/toPreviewSections';
import { useStatutes, type Statute } from './useStatutes';

function deriveStoragePathFromFileUrl(url?: string): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const marker = '/o/';
    const idx = parsed.pathname.indexOf(marker);
    if (idx === -1) return null;
    const encodedPath = parsed.pathname.slice(idx + marker.length);
    const decoded = decodeURIComponent(encodedPath || '');
    return decoded || null;
  } catch {
    return null;
  }
}

function parseStatuteSections(
  statute: Statute,
  remoteContent: string | null,
): StatutePreviewUiSection[] {
  const contentText =
    typeof statute.content === 'string' && statute.content.trim()
      ? statute.content
      : remoteContent || '';
  if (!contentText) return [];

  try {
    const json = JSON.parse(contentText);
    const canonical = adaptStatuteAny(json, statute.category);
    const bridged = toPreviewSections(canonical);
    if (bridged.length) return bridged as StatutePreviewUiSection[];
  } catch {
    // fall through to legacy parser
  }

  const base = parseStatuteTextForReader(contentText);
  if (base.length) {
    return normalizePreviewSections(base as Record<string, unknown>[]).map((section, i) => ({
      ...section,
      tocListIndex: i + 1,
    })) as StatutePreviewUiSection[];
  }

  return [
    {
      id: 'full-text',
      label: 'DOCUMENT',
      title: statute.title,
      content: contentText,
      tocListIndex: 1,
    },
  ] as StatutePreviewUiSection[];
}

export function findStatuteSection(
  sections: StatutePreviewUiSection[],
  sectionId: string | null,
): StatutePreviewUiSection | null {
  if (!sections.length) return null;
  if (!sectionId) return sections[0] ?? null;

  const normalized = sectionId.replace(/^section-/i, '');
  return (
    sections.find((section) => {
      const sid = String(section.id || '');
      const sidNorm = sid.replace(/^section-/i, '');
      return (
        sid === sectionId ||
        sid === normalized ||
        sidNorm === normalized ||
        sidNorm === sectionId.replace(/^section-/i, '')
      );
    }) ?? null
  );
}

export function useStatuteReaderSections(statuteId: string | null) {
  const { statutes, loading: listLoading } = useStatutes();
  const statute = useMemo(
    () => statutes.find((item) => item.id === statuteId) ?? null,
    [statutes, statuteId],
  );
  const [remoteContent, setRemoteContent] = useState<string | null>(null);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteError, setRemoteError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadRemoteContent = async () => {
      setRemoteContent(null);
      setRemoteError(null);
      if (!statute) return;

      const inline = typeof statute.content === 'string' ? statute.content.trim() : '';
      if (inline) return;

      setRemoteLoading(true);
      try {
        const pathFromUrl = deriveStoragePathFromFileUrl(
          statute.fileUrl || statute.documentUrl || statute.pdfUrl,
        );
        const path = statute.storagePath || pathFromUrl;
        if (!path) throw new Error('No storage path found for this statute.');
        const bytes = await getBytes(storageRef(storage, path));
        const text = new TextDecoder().decode(bytes);
        if (!cancelled) setRemoteContent(text);
      } catch (err) {
        console.error('Failed to load remote statute content:', err);
        if (!cancelled) setRemoteError('Failed to load statute content from storage.');
      } finally {
        if (!cancelled) setRemoteLoading(false);
      }
    };

    void loadRemoteContent();
    return () => {
      cancelled = true;
    };
  }, [statute]);

  const sections = useMemo(
    () => (statute ? parseStatuteSections(statute, remoteContent) : []),
    [statute, remoteContent],
  );

  const hasInlineContent = Boolean(
    statute && typeof statute.content === 'string' && statute.content.trim(),
  );
  const loading = listLoading || (!hasInlineContent && remoteLoading);

  return {
    statute,
    sections,
    loading,
    error: remoteError,
  };
}
